/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **What makes an object in an ORTB body template a mapping rather than a structure** — one
 * declaration, one predicate (`#1198`).
 *
 * The ORTB body is a **template**, not a payload. A mapped value is stored as an object carrying a
 * `$`-prefixed discriminator, and `OpenRtbDriver.resolveTemplateNode` replaces the whole node with
 * what it resolves to before the merge. Safety is **recognition**, never the `$` prefix: a stray
 * `$foo` on an ordinary object is walked and emitted like any other key.
 *
 * ## Why this is shared, measured rather than argued
 *
 * The vocabulary was declared **twice** — `openrtb.driver.ts` and the console's
 * `ortb-body-editor.adapters.ts` — and the two lists **already disagreed**: the console recognised
 * `$optional` and the driver did not, so a node carrying only that key was walked as a structure
 * and `{"$optional": false}` went out to the exchange as a body field. Nothing failed; nothing
 * could.
 *
 * And a **third** reader had no copy at all, which is the defect that made this urgent.
 * `checkDestinationValue` grades an authored literal against the type its destination declares,
 * and the ORTB body validation sweep hands it whatever sits at the field's path. For any mapped
 * field that is a **node**, that is an object — so a correct mapping reported
 * *"`content.genre` expects text and this is an object"*, on every mapped field, at once. Thirteen
 * errors on an ordinary demand source, all false, all on the surface `R3a` had just been extended
 * to.
 *
 * That is **`D9`**: the checker's own docstring already carries the rule — *"the literal carries a
 * macro … not gradeable until it resolves … grading a token here would report an error on every
 * correct mapping"* — written for the **string** spelling of a deferred value, at a time when that
 * was the only one. The node spelling arrived later and the rule did not follow it, so the rule was
 * true and too narrow, and nothing contradicted it.
 *
 * ## Zero imports, deliberately
 *
 * `$expr` is typed `unknown` here rather than `MacroExpression`, which would drag the whole DTO
 * graph across the console's import boundary (`shared-modules.spec.ts` counts type-only imports,
 * because `tsc -b` still has to resolve them). Both readers already narrow it locally, and what
 * they must not each decide is **which keys make a node** — which is what this file holds.
 */

/**
 * The keys that make an object a leaf rather than a structure to descend into.
 *
 * Keyed on the **discriminators**: `$valueType` (and its old spelling `$type`) and `$default` qualify nothing on their own, and
 * treating a stray one as a node would swallow a real object and drop everything under it.
 *
 * `$optional` is here because it is one — it is an authoring claim that must be stripped with the
 * rest of the node rather than emitted. Adding it is a **behaviour change for the driver**, and the
 * behaviour it changes is a leak. `$importance`, which replaces it (`X46`), is the same kind of
 * claim and is listed for the same reason: a node carrying only that key must not be walked as a
 * structure and shipped to the exchange. `$acceptSystemDefault` (`X33a`, `#1266`) is the third of
 * that kind.
 */
import type { DestinationFieldType } from './destination-field';

export const ORTB_BODY_LEAF_KEYS = [
  '$macro',
  '$expr',
  '$static',
  '$suppress',
  '$optional',
  '$importance',
  '$acceptSystemDefault',
  '$secret',
  '$sendAs',
] as const;

/**
 * A mapped value in an ORTB body template.
 *
 * Declared with `$expr` as `unknown` — see the module docstring. Narrow it where you have the
 * expression type.
 */
export interface OrtbBodyLeafNode {
  /** The macro token, without braces. */
  $macro?: string;
  /** The structured expression — a `MacroExpression`, narrowed by the reader. */
  $expr?: unknown;
  /** An authored literal that needed a node because it carries a property beside it. */
  $static?: unknown;
  /**
   * **What this leaf's value is** — the row's `Type`, which overrides the expression's own
   * `asType` (`FD11` §5, `4e`). A declaration: the driver coerces to a scalar member and leaves a
   * structured one as it stands (`expression-yield.ts`).
   */
  $valueType?: DestinationFieldType;
  /**
   * **Pre-`1f-iii` spelling of {@link OrtbBodyLeafNode.$valueType}**, read and no longer written
   * (`FD3a`). Renamed so the row's claim and the destination's declaration (`DestinationField.valueType`)
   * share one name. Read through {@link ortbBodyLeafValueType}, the one place both spellings are known.
   */
  $type?: DestinationFieldType;
  /** What to send when the mapping resolves to nothing (`N11f`). */
  $default?: string;
  /**
   * **How a list goes out where this field takes a single value** (`4f`, `#1416`) — the row's *Send
   * structured values as*, a `WireRendering`, narrowed by `normalizeWireRendering` where it is read.
   * Inert where the field takes a list: `renderForWire` sends a list as a list there. Stripped with the
   * rest of the node and never emitted.
   */
  $sendAs?: unknown;
  /** Never send this key, and strip whatever the driver built at this path. */
  $suppress?: boolean;
  /**
   * **Let the value the driver built at this path stand when this leaf resolves to nothing**
   * (`X33a`, `#1266`) — `false` only, because absence is *accept* and that is what the driver has
   * always done.
   *
   * **Distinct from {@link OrtbBodyLeafNode.$suppress}, and the difference is the condition.**
   * `$suppress` strips whatever happens; this strips **only when my own value did not resolve**.
   * Collapsing them would make a typo'd macro silently delete a driver-derived field, which is the
   * exact defect `UNRESOLVED` was split from `SUPPRESSED` to prevent (`#880`).
   */
  $acceptSystemDefault?: false;
  /**
   * **Pre-`X46` spelling of {@link OrtbBodyLeafNode.$importance}, read and no longer written.**
   *
   * It was never an omission answer *here*: an ORTB body key that resolves to nothing is omitted
   * either way (#880), so on this surface the flag decided nothing on the wire and was purely the
   * required-ness claim. That is what made the two concepts visibly separate and is why `X46`
   * splits them — see `resolveFieldImportance`, the one place this is read.
   */
  $optional?: false;
  /**
   * **How much this destination wants a value here** (`X46`) — the operator's claim about the
   * field, stripped with the rest of the node and never emitted.
   */
  $importance?: 'required' | 'recommended' | 'optional';
  /**
   * **Whether this row's authored literal is a credential** (`CR2`, `#1371`). Declared on the row
   * rather than matched by name; `suggestsSecretValue` seeds it. Only the literal is protected: it is
   * encrypted at the artifact write boundary, decrypted where the document is read, and never read back
   * by the console (`CR3`, `CR4`). A row that resolves from a macro, a path or an expression has no
   * stored secret, so the flag protects nothing there and costs nothing.
   * Stripped with the rest of the node and never emitted.
   */
  $secret?: true;
  /** Pre-`#1216` spelling of {@link OrtbBodyLeafNode.$default} (`X3`), read so a stored document resolves. */
  $fallback?: string;
  /** Pre-node spelling of {@link OrtbBodyLeafNode.$default}, read so an older document resolves. */
  fallback?: string;
}

/**
 * **The one place a leaf's default is read** (`X3`, `#1216`).
 *
 * Three spellings, newest first: `$default` is the word, `$fallback` is what it was called until
 * 2026-09-02, and a bare `fallback` predates the node shape entirely. A stored body template is
 * not rewritten until it is next saved, so all three have to resolve — and reading them here
 * rather than at each call site is why the driver and the console cannot come to disagree about
 * which one wins.
 */
export function ortbBodyLeafDefault(node: OrtbBodyLeafNode): string | undefined {
  return node.$default !== undefined ? node.$default : (node.$fallback ?? node.fallback);
}

/**
 * **The one place a leaf's value type is read** (`FD3a`) — `$valueType`, else the `$type` a stored
 * template carries until it is next saved. The `X3` pattern: one legacy read per store, so the driver
 * and the console cannot disagree about which spelling wins.
 */
export function ortbBodyLeafValueType(node: OrtbBodyLeafNode): DestinationFieldType | undefined {
  return node.$valueType !== undefined ? node.$valueType : node.$type;
}

/** Is this a mapped value, rather than a structure to descend into? */
export function isOrtbBodyLeafNode(value: unknown): value is OrtbBodyLeafNode {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  return ORTB_BODY_LEAF_KEYS.some((k) => k in (value as Record<string, unknown>));
}

// ────────────────────────────────────────────────────────────────────────────────────────────────
// The pattern node — *every element the driver builds*, and *this one* (RS4c, `#918`, slice `1d`)
// ────────────────────────────────────────────────────────────────────────────────────────────────

/**
 * **Every element the driver builds, however many that turns out to be.** Beside it,
 * {@link ORTB_BODY_PATTERN_AT} overrides one element, keyed by index or {@link ORTB_BODY_PATTERN_LAST}.
 *
 * Declared beside the leaf keys because it answers the same question from the other side: *what
 * makes an object in a body template something other than a structure to emit* — a leaf is replaced
 * by what it resolves to, a pattern is applied to the array the driver built at its path.
 */
export const ORTB_BODY_PATTERN_EACH = '$each';
export const ORTB_BODY_PATTERN_AT = '$at';
/**
 * **The last element the driver builds** — the highest index at the moment the request is built
 * (RS4b, ruled 2026-09-18). The same word `readOwnPath` resolves on a read path (`own-path.ts`), kept
 * as a literal here because this file imports nothing.
 */
export const ORTB_BODY_PATTERN_LAST = 'last';

/**
 * The paths whose authored **array** is folded into a pattern where a document enters
 * ({@link foldOrtbBodyPattern}). Only `imp`: it is the one array the guided editor authors, and
 * folding every array of objects would change what an authored `user.eids` means where the driver
 * builds none. The driver's application is generic, so another path adopting the pattern is an
 * addition here and nowhere else.
 */
export const ORTB_BODY_PATTERN_PATHS: readonly string[] = ['imp'];

/** An authored pattern over an array the driver builds. */
export interface OrtbBodyPatternNode {
  $each?: Record<string, unknown>;
  $at?: Record<string, Record<string, unknown>>;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Is this a pattern rather than a structure? **Recognised by shape**, as a leaf is: an object whose
 * keys are only `$each` and `$at`, holding objects. A stray `$each` beside ordinary keys is a
 * structure and is emitted as one.
 */
export function isOrtbBodyPatternNode(value: unknown): value is OrtbBodyPatternNode {
  if (!isPlainRecord(value)) return false;
  const keys = Object.keys(value);
  if (keys.length === 0) return false;
  if (!keys.every((k) => k === ORTB_BODY_PATTERN_EACH || k === ORTB_BODY_PATTERN_AT)) return false;
  if (value.$each !== undefined && !isPlainRecord(value.$each)) return false;
  if (value.$at !== undefined && (!isPlainRecord(value.$at) || !Object.values(value.$at).every(isPlainRecord))) {
    return false;
  }
  return true;
}

/** Is this an `$at` key — an index or `last`? */
export function isOrtbBodyPatternPosition(key: string): boolean {
  return key === ORTB_BODY_PATTERN_LAST || /^\d+$/.test(key);
}

/**
 * The element an `$at` key addresses among `length` built elements, or `null` where it addresses
 * none — **an index past what the driver built is not applied**: authoring decorates, it never sets
 * the count (RS3).
 */
export function ortbBodyPatternIndex(key: string, length: number): number | null {
  if (key === ORTB_BODY_PATTERN_LAST) return length > 0 ? length - 1 : null;
  if (!/^\d+$/.test(key)) return null;
  const index = Number(key);
  return index < length ? index : null;
}

/**
 * **Apply a pattern to the elements the driver built.** Each element is copied, `$each` is merged
 * onto it, then every `$at` addressing it, in index-then-`last` order so a `last` override wins over
 * a numeric one naming the same element. `merge` is the caller's deep merge, so the two surfaces that
 * apply a pattern cannot disagree about what merging an element means.
 */
export function applyOrtbBodyPattern(
  built: readonly unknown[],
  pattern: OrtbBodyPatternNode,
  merge: (target: Record<string, unknown>, src: Record<string, unknown>) => void,
): unknown[] {
  const at = Object.entries(pattern.$at ?? {}).sort(([a], [b]) =>
    a === ORTB_BODY_PATTERN_LAST ? 1 : b === ORTB_BODY_PATTERN_LAST ? -1 : Number(a) - Number(b),
  );
  return built.map((element, index) => {
    if (!isPlainRecord(element)) return element;
    // **A deep copy, because the driver's elements share their nested objects** — it builds a pod's
    // impressions around one `video`, so merging a per-slot override into a shallow copy wrote it
    // through to every slot. Measured by the first `$at` spec, which saw `last`'s value on `imp[0]`.
    const merged = structuredClone(element);
    // …and a copy of what is merged in: where an element has no `ext` yet, a merge assigns the
    // authored object itself, so every slot would hold one `ext` and a `last` override on it would
    // land on all of them.
    if (pattern.$each) merge(merged, structuredClone(pattern.$each));
    for (const [key, override] of at) {
      if (ortbBodyPatternIndex(key, built.length) === index) merge(merged, structuredClone(override));
    }
    return merged;
  });
}

/**
 * The element indices a path segment under a pattern addresses: every one for `$each`, one for an
 * `$at` key. Used to carry a **suppression** authored inside a pattern to the elements it names.
 */
export function ortbBodyPatternTargets(segment: string, key: string | undefined, length: number): number[] {
  if (segment === ORTB_BODY_PATTERN_EACH) return Array.from({ length }, (_, i) => i);
  if (segment === ORTB_BODY_PATTERN_AT && key !== undefined) {
    const index = ortbBodyPatternIndex(key, length);
    return index === null ? [] : [index];
  }
  return [];
}

export interface OrtbBodyPatternFold<T> {
  /** A new template; the input is not mutated. Identical by reference when nothing moved. */
  template: T;
  /** One line per move, for a repair report. */
  moves: string[];
  /**
   * Whether the folded template sends the same request. A one-element array always did broadcast,
   * so it folds to `$each` unchanged — **unless it carries a suppression**, which the old merge
   * applied to the first impression only. Several elements merged by index and **appended** any
   * past the built count, which `$at` does not, and the built count is not knowable here — so that
   * fold cannot promise the same request, and says so.
   */
  wirePreserving: boolean;
}

function carriesSuppression(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(carriesSuppression);
  if (!isPlainRecord(value)) return false;
  if (value.$suppress === true) return true;
  return Object.values(value).some(carriesSuppression);
}

/**
 * **Fold an authored `imp` array into the pattern it meant** (RS4c) — where a document enters: the
 * admin write normalizer and the repair, as `foldRetiredStaticParams` is. Only in `merge` mode: a
 * `replace` body is the operator's whole request, so its arrays are literal.
 *
 * One element becomes `$each` (the broadcast it always was); several become `$at` by index. The
 * body's `provenance` side-car is keyed by dotted path, so its keys move with the values they
 * describe, or every mark on a folded field would read as stale.
 */
export function foldOrtbBodyPattern<
  T extends { mode?: string; body?: Record<string, unknown>; provenance?: Record<string, unknown> },
>(template: T): OrtbBodyPatternFold<T> {
  const body = template.body;
  if (!isPlainRecord(body) || template.mode === 'replace') return { template, moves: [], wirePreserving: true };

  let nextBody: Record<string, unknown> | undefined;
  let nextProvenance: Record<string, unknown> | undefined;
  const moves: string[] = [];
  let wirePreserving = true;

  for (const path of ORTB_BODY_PATTERN_PATHS) {
    const value = body[path];
    if (!Array.isArray(value) || value.length === 0 || !value.every(isPlainRecord)) continue;

    const rename: Array<[string, string]> = [];
    let node: OrtbBodyPatternNode;
    if (value.length === 1) {
      node = { $each: value[0] };
      rename.push([`${path}.0`, `${path}.${ORTB_BODY_PATTERN_EACH}`]);
      if (carriesSuppression(value[0])) {
        wirePreserving = false;
        moves.push(
          `folded ${path}[0] into ${path}.$each — its suppression now strips every impression, where it stripped the first`,
        );
      } else {
        moves.push(`folded ${path}[0] into ${path}.$each — it already applied to every impression`);
      }
    } else {
      node = { $at: Object.fromEntries(value.map((el, i) => [String(i), el])) };
      value.forEach((_, i) => rename.push([`${path}.${i}`, `${path}.${ORTB_BODY_PATTERN_AT}.${i}`]));
      wirePreserving = false;
      moves.push(
        `folded ${value.length} ${path} elements into ${path}.$at by index — an element past the impressions the driver builds is no longer appended`,
      );
    }

    nextBody = { ...(nextBody ?? body), [path]: node };
    if (isPlainRecord(template.provenance)) {
      const source: Record<string, unknown> = nextProvenance ?? template.provenance;
      nextProvenance = Object.fromEntries(
        Object.entries(source).map(([key, entry]) => {
          const hit = rename.find(([from]) => key === from || key.startsWith(`${from}.`));
          return [hit ? hit[1] + key.slice(hit[0].length) : key, entry];
        }),
      );
    }
  }

  if (!nextBody) return { template, moves: [], wirePreserving: true };
  return {
    template: { ...template, body: nextBody, ...(nextProvenance ? { provenance: nextProvenance } : {}) },
    moves,
    wirePreserving,
  };
}

/**
 * **{@link foldOrtbBodyPattern} over a demand source document** — the one call every entry point
 * makes (the admin write normalizer, the demo service, the legacy-config adapter, and `R7`), so a
 * record saved through the console and one repaired at rest end in the same shape. The input is not
 * mutated; it is returned by reference when there was nothing to fold.
 */
export function foldOrtbBodyPatternOf<T extends Record<string, unknown>>(document: T): OrtbBodyPatternFold<T> {
  const template = document.ortbBodyTemplate;
  if (!isPlainRecord(template)) return { template: document, moves: [], wirePreserving: true };
  const fold = foldOrtbBodyPattern(template);
  if (fold.template === template) return { template: document, moves: [], wirePreserving: true };
  return {
    template: { ...document, ortbBodyTemplate: fold.template },
    moves: fold.moves,
    wirePreserving: fold.wirePreserving,
  };
}
